import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type MemberReflectionBackfillMemberRow,
  type MemberReflectionBackfillRunItem,
  type MemberReflectionBackfillRunResponse,
  type MemberReflectionBackfillSessionRow,
  type MemberReflectionBackfillSummary,
  useCreateMemberReflectionBackfillRunMutation,
  useGetMemberReflectionBackfillMembersQuery,
  useGetMemberReflectionBackfillPreviousFailuresQuery,
  useGetMemberReflectionBackfillReadinessGapsQuery,
  useGetMemberReflectionBackfillRunQuery,
  useGetMemberReflectionBackfillSummaryQuery,
  usePrepareMemberReflectionBackfillReadinessMutation,
  useRetryMemberReflectionBackfillPreviousFailuresMutation,
  useRetryMemberReflectionBackfillRunMutation,
  useReviewMemberReflectionBackfillMutation,
} from './api/memberReflectionBackfillApi';

const defaultLimit = 100;

interface DashboardProps {
  summary?: MemberReflectionBackfillSummary;
  members?: MemberReflectionBackfillMemberRow[];
  readinessGaps?: MemberReflectionBackfillSessionRow[];
  reviewSample?: MemberReflectionBackfillSessionRow[];
  runResponse?: MemberReflectionBackfillRunResponse;
  previousFailures?: MemberReflectionBackfillRunItem[];
  limit: number;
  isLoading?: boolean;
  errorMessage?: string;
  actionMessage?: string;
  canCreateRun?: boolean;
  onLimitChange?: (limit: number) => void;
  onRefresh?: () => void;
  onReview?: () => void;
  onPrepareReadiness?: () => void;
  onCreateRun?: () => void;
  onRetryRun?: () => void;
  onRetryPreviousFailures?: () => void;
}

export function MemberReflectionBackfillDashboard({
  summary,
  members = [],
  readinessGaps = [],
  reviewSample = [],
  runResponse,
  previousFailures = [],
  limit,
  isLoading = false,
  errorMessage,
  actionMessage,
  canCreateRun = false,
  onLimitChange,
  onRefresh,
  onReview,
  onPrepareReadiness,
  onCreateRun,
  onRetryRun,
  onRetryPreviousFailures,
}: DashboardProps) {
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Member reflection backfill
          </Typography>
          <Typography color="text.secondary">
            Review historical transcript-bearing sessions, prepare readiness gaps, and create a
            bounded pre-flag backfill run.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh} variant="outlined">
          Refresh
        </Button>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {actionMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {actionMessage}
        </Alert>
      )}
      {isLoading && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryCard
          label="Members / sessions"
          value={`${summary?.total_members ?? 0} / ${summary?.transcript_bearing_sessions ?? 0}`}
        />
        <SummaryCard
          label="Needs backfill"
          value={summary?.needs_backfill_sessions ?? 0}
          tone="primary"
        />
        <SummaryCard
          label="Readiness gaps"
          value={summary?.readiness_gap_sessions ?? 0}
          tone="warning"
        />
        <SummaryCard
          label="Latest-session gaps"
          value={summary?.latest_session_readiness_gaps ?? 0}
          tone="warning"
        />
        <SummaryCard
          label="Queued / running"
          value={`${summary?.queued_sessions ?? 0} / ${summary?.running_sessions ?? 0}`}
        />
        <SummaryCard label="Reflected" value={summary?.reflected_sessions ?? 0} tone="success" />
        <SummaryCard
          label="Failed / blocked"
          value={`${summary?.failed_sessions ?? 0} / ${summary?.blocked_sessions ?? 0}`}
          tone="error"
        />
        <SummaryCard label="Latest missing" value={summary?.latest_session_missing ?? 0} />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Review and run controls
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Limit"
            type="number"
            size="small"
            value={limit}
            inputProps={{ min: 1 }}
            onChange={(event) => onLimitChange?.(Number(event.target.value))}
          />
          <Button variant="outlined" onClick={onReview}>
            Review sample
          </Button>
          <Button
            variant="outlined"
            onClick={onPrepareReadiness}
            disabled={(summary?.readiness_gap_sessions ?? 0) === 0}
          >
            Prepare readiness gaps
          </Button>
          <Button variant="contained" onClick={onCreateRun} disabled={!canCreateRun}>
            Create backfill run
          </Button>
          <Typography variant="body2" color="text.secondary">
            Run creation only persists current needs-backfill items; it does not start generation
            jobs directly.
          </Typography>
        </Box>
      </Paper>

      <MembersTable members={members} />

      {readinessGaps.length > 0 && <SessionTable title="Readiness gaps" rows={readinessGaps} />}
      {reviewSample.length > 0 && <SessionTable title="Review sample" rows={reviewSample} />}
      <PreviousFailuresPanel
        items={previousFailures}
        onRetryPreviousFailures={onRetryPreviousFailures}
      />
      {runResponse && <RunPanel runResponse={runResponse} onRetryRun={onRetryRun} />}
    </Box>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'primary' | 'success' | 'warning' | 'error';
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" color={tone ? `${tone}.main` : 'text.primary'}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function MembersTable({ members }: { members: MemberReflectionBackfillMemberRow[] }) {
  return (
    <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Members</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell align="right">Sessions</TableCell>
            <TableCell align="right">Needs</TableCell>
            <TableCell align="right">Gaps</TableCell>
            <TableCell align="right">Queued/running</TableCell>
            <TableCell>Latest</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.member_id} hover>
              <TableCell>
                <Typography variant="body2">{member.member_name || member.member_email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {member.member_email}
                </Typography>
              </TableCell>
              <TableCell align="right">{member.transcript_bearing_sessions}</TableCell>
              <TableCell align="right">{member.needs_backfill_sessions}</TableCell>
              <TableCell align="right">{member.readiness_gap_sessions}</TableCell>
              <TableCell align="right">
                {member.queued_sessions + member.running_sessions}
              </TableCell>
              <TableCell>
                <StatusChip status={member.latest_session_status} />
                {member.latest_session_at && (
                  <Typography variant="caption" sx={{ display: 'block' }} color="text.secondary">
                    {formatDate(member.latest_session_at)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function SessionTable({
  title,
  rows,
}: {
  title: string;
  rows: MemberReflectionBackfillSessionRow[];
}) {
  return (
    <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Member</TableCell>
            <TableCell>Session</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.member_id}-${row.session_id}`} hover>
              <TableCell>{row.member_email}</TableCell>
              <TableCell>{formatDate(row.session_at)}</TableCell>
              <TableCell>
                <StatusChip status={row.status} />
              </TableCell>
              <TableCell>
                {row.blocked_reason || row.error_message || row.eligibility_status}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function PreviousFailuresPanel({
  items,
  onRetryPreviousFailures,
}: {
  items: MemberReflectionBackfillRunItem[];
  onRetryPreviousFailures?: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6">Previous failed/blocked items</Typography>
          <Typography variant="body2" color="text.secondary">
            Recent failed or blocked run items from earlier backfill runs. Retrying rechecks them
            and returns currently eligible items to the dispatcher.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onRetryPreviousFailures} disabled={items.length === 0}>
          Retry listed failures
        </Button>
      </Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No previous failed or blocked run items in this sample.
        </Typography>
      ) : (
        <RunItemTable items={items} showRun />
      )}
    </Paper>
  );
}

function RunPanel({
  runResponse,
  onRetryRun,
}: {
  runResponse: MemberReflectionBackfillRunResponse;
  onRetryRun?: () => void;
}) {
  const { run, items } = runResponse;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6">Run progress</Typography>
          <Typography variant="body2" color="text.secondary">
            {run.id}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onRetryRun}
          disabled={run.failed_items + run.blocked_items === 0}
        >
          Retry failed/blocked
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <StatusChip status={run.status} />
        <Chip
          label={`pending ${run.pending_items}`}
          color={run.pending_items ? 'info' : 'default'}
        />
        <Chip label={`queued ${run.queued_items}`} />
        <Chip label={`running ${run.running_items}`} />
        <Chip label={`completed ${run.completed_items}`} />
        <Chip label={`satisfied ${run.already_satisfied_items}`} />
        <Chip
          label={`blocked ${run.blocked_items}`}
          color={run.blocked_items ? 'warning' : 'default'}
        />
        <Chip label={`failed ${run.failed_items}`} color={run.failed_items ? 'error' : 'default'} />
      </Box>
      <RunItemTable items={items} />
    </Paper>
  );
}

function RunItemTable({
  items,
  showRun = false,
}: {
  items: MemberReflectionBackfillRunItem[];
  showRun?: boolean;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {showRun && <TableCell>Run</TableCell>}
          <TableCell>Session</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Job / artifact</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id} hover>
            {showRun && <TableCell>{item.run_id}</TableCell>}
            <TableCell>{item.session_id}</TableCell>
            <TableCell>
              <StatusChip status={item.status} />
            </TableCell>
            <TableCell>{item.job_id || item.artifact_id || '—'}</TableCell>
            <TableCell>
              {item.blocked_reason || item.error_message || item.eligibility_status}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function StatusChip({ status }: { status: string }) {
  const color = status.includes('failed')
    ? 'error'
    : status.includes('blocked') || status.includes('gap') || status.includes('missing')
      ? 'warning'
      : status.includes('completed') || status.includes('reflected') || status.includes('satisfied')
        ? 'success'
        : status.includes('running') || status.includes('queued') || status.includes('pending')
          ? 'primary'
          : 'default';
  return <Chip size="small" label={status} color={color} />;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

function errorToMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  const queryError = error as FetchBaseQueryError;
  if (typeof queryError.status !== 'undefined') return `Request failed: ${queryError.status}`;
  return 'Request failed';
}

export function memberReflectionBackfillRunIDFromSearch(search: string): string | undefined {
  const value = new URLSearchParams(search).get('run_id')?.trim();
  return value || undefined;
}

export default function MemberReflectionBackfillPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(defaultLimit);
  const [actionMessage, setActionMessage] = useState<string | undefined>();
  const [reviewSample, setReviewSample] = useState<MemberReflectionBackfillSessionRow[]>([]);
  const runId = memberReflectionBackfillRunIDFromSearch(searchParams.toString());
  const scope = useMemo(() => ({ limit }), [limit]);

  const summaryQuery = useGetMemberReflectionBackfillSummaryQuery(scope);
  const membersQuery = useGetMemberReflectionBackfillMembersQuery(scope);
  const gapsQuery = useGetMemberReflectionBackfillReadinessGapsQuery(scope);
  const previousFailuresQuery = useGetMemberReflectionBackfillPreviousFailuresQuery(scope);
  const runQuery = useGetMemberReflectionBackfillRunQuery(runId || '', { skip: !runId });
  const [review, reviewState] = useReviewMemberReflectionBackfillMutation();
  const [prepareReadiness, prepareState] = usePrepareMemberReflectionBackfillReadinessMutation();
  const [createRun, createRunState] = useCreateMemberReflectionBackfillRunMutation();
  const [retryRun, retryRunState] = useRetryMemberReflectionBackfillRunMutation();
  const [retryPreviousFailures, retryPreviousFailuresState] =
    useRetryMemberReflectionBackfillPreviousFailuresMutation();

  const isLoading =
    summaryQuery.isLoading ||
    membersQuery.isLoading ||
    gapsQuery.isLoading ||
    previousFailuresQuery.isLoading;
  const errorMessage =
    errorToMessage(summaryQuery.error) ||
    errorToMessage(membersQuery.error) ||
    errorToMessage(gapsQuery.error) ||
    errorToMessage(previousFailuresQuery.error);

  async function refresh() {
    await Promise.all([
      summaryQuery.refetch(),
      membersQuery.refetch(),
      gapsQuery.refetch(),
      previousFailuresQuery.refetch(),
    ]);
    if (runId) await runQuery.refetch();
  }

  async function handleReview() {
    const result = await review(scope).unwrap();
    setReviewSample(result.sample);
    setActionMessage(
      `Reviewed ${result.summary.transcript_bearing_sessions} transcript-bearing sessions.`
    );
  }

  async function handlePrepareReadiness() {
    const result = await prepareReadiness(scope).unwrap();
    setActionMessage(
      `Prepared ${result.evaluated_count} readiness gaps; ${result.became_ready_count} became ready.`
    );
  }

  async function handleCreateRun() {
    const result = await createRun(scope).unwrap();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('run_id', result.run.id);
    setSearchParams(nextParams, { replace: true });
    setActionMessage(`Created run ${result.run.id} with ${result.run.total_items} items.`);
  }

  async function handleRetryRun() {
    if (!runId) return;
    const result = await retryRun(runId).unwrap();
    setActionMessage(`Retry rechecked ${result.items.length} items.`);
    await Promise.all([runQuery.refetch(), previousFailuresQuery.refetch()]);
  }

  async function handleRetryPreviousFailures() {
    const listedItems = previousFailuresQuery.data?.items ?? [];
    const result = await retryPreviousFailures({
      items: listedItems.map((item) => ({
        id: item.id,
        status: item.status,
        updated_at: item.updated_at,
      })),
    }).unwrap();
    setActionMessage(
      `Retry rechecked ${result.items.length} listed failed/blocked items; ${result.retryable_reset_count} returned to pending.`
    );
    await Promise.all([
      previousFailuresQuery.refetch(),
      summaryQuery.refetch(),
      membersQuery.refetch(),
      runId ? runQuery.refetch() : Promise.resolve(),
    ]);
  }

  return (
    <MemberReflectionBackfillDashboard
      summary={summaryQuery.data}
      members={membersQuery.data}
      readinessGaps={gapsQuery.data?.rows}
      reviewSample={reviewSample}
      runResponse={runQuery.data}
      previousFailures={previousFailuresQuery.data?.items}
      limit={limit}
      isLoading={
        isLoading ||
        reviewState.isLoading ||
        prepareState.isLoading ||
        createRunState.isLoading ||
        retryRunState.isLoading ||
        retryPreviousFailuresState.isLoading
      }
      errorMessage={
        errorMessage ||
        errorToMessage(reviewState.error) ||
        errorToMessage(prepareState.error) ||
        errorToMessage(createRunState.error) ||
        errorToMessage(retryRunState.error) ||
        errorToMessage(retryPreviousFailuresState.error)
      }
      actionMessage={actionMessage}
      canCreateRun={(summaryQuery.data?.needs_backfill_sessions ?? 0) > 0}
      onLimitChange={(nextLimit) =>
        setLimit(Number.isFinite(nextLimit) && nextLimit > 0 ? nextLimit : defaultLimit)
      }
      onRefresh={refresh}
      onReview={handleReview}
      onPrepareReadiness={handlePrepareReadiness}
      onCreateRun={handleCreateRun}
      onRetryRun={handleRetryRun}
      onRetryPreviousFailures={handleRetryPreviousFailures}
    />
  );
}
